import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PASSWORD_RULES,
  passwordPolicyFailures,
  type PasswordRule,
} from '@lims/shared-validation';
import { TextField } from './fields.js';
import { MarkAbsent, MarkInTolerance } from './marks.js';

/**
 * Password entry.
 *
 * Every place a password is typed uses these: signing a colleague up, a
 * manager setting a new one, a person changing their own, and the reset-link
 * page. The policy shown is the SHARED policy (`passwordPolicyFailures` in
 * `@lims/shared-validation`) — the list beside the field is built from the same
 * rules the server enforces, so the form can never promise a password the API
 * will then refuse.
 *
 * The rule list is a checklist with a drawn mark per rule, not a colour: met
 * and unmet differ by the mark, the word and the ink together, and each item
 * says "met" or "not met" to a screen reader.
 */

/** A password box with a show/hide control. */
export function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  description,
  error,
  isRequired = false,
  isDisabled = false,
  name,
}: {
  /** Already translated. */
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** `new-password` or `current-password`, so managers do not get autofill. */
  readonly autoComplete: 'new-password' | 'current-password';
  readonly description?: string;
  readonly error?: string;
  readonly isRequired?: boolean;
  readonly isDisabled?: boolean;
  readonly name?: string;
}) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  return (
    <TextField
      type={isVisible ? 'text' : 'password'}
      label={label}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      isRequired={isRequired}
      isDisabled={isDisabled}
      {...(name ? { name } : {})}
      {...(description ? { description } : {})}
      {...(error ? { error } : {})}
      endContent={
        // `type="button"`: inside a form, a bare button would SUBMIT it.
        <button
          type="button"
          onClick={() => setIsVisible((current) => !current)}
          aria-pressed={isVisible}
          aria-label={isVisible ? t('password.hide') : t('password.show')}
          disabled={isDisabled}
          className="rounded-small px-1 text-caption font-medium text-primary-800 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          {isVisible ? t('password.hide') : t('password.show')}
        </button>
      }
    />
  );
}

/** The live checklist of the shared password rules. */
export function PasswordRules({ value }: { readonly value: string }) {
  const { t } = useTranslation();
  const unmet = new Set<PasswordRule>(passwordPolicyFailures(value));

  return (
    <ul
      className="grid gap-1 text-caption sm:grid-cols-2"
      aria-label={t('password.rulesTitle')}
    >
      {PASSWORD_RULES.map((rule) => {
        const isMet = !unmet.has(rule);
        return (
          <li
            key={rule}
            className={`flex items-center gap-inline ${
              isMet ? 'text-success-700' : 'text-default-600'
            }`}
          >
            <span aria-hidden="true" className="shrink-0">
              {isMet ? <MarkInTolerance size={12} /> : <MarkAbsent size={12} />}
            </span>
            <span>{t(`password.rule.${rule}`)}</span>
            <span className="sr-only">
              {isMet ? t('password.ruleMet') : t('password.ruleUnmet')}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * What is wrong with a new password and its confirmation, if anything.
 *
 * `null` means both are acceptable. Callers show the message only after a
 * submit attempt, so a half-typed password is not treated as a mistake.
 */
export type NewPasswordProblem = 'required' | 'rules' | 'mismatch';

export function newPasswordProblem(
  password: string,
  confirmation: string,
): { readonly newPassword?: NewPasswordProblem; readonly confirmation?: NewPasswordProblem } {
  if (password === '') return { newPassword: 'required' };
  if (passwordPolicyFailures(password).length > 0) return { newPassword: 'rules' };
  if (confirmation === '') return { confirmation: 'required' };
  if (confirmation !== password) return { confirmation: 'mismatch' };
  return {};
}

export function hasNewPasswordProblem(password: string, confirmation: string): boolean {
  const problem = newPasswordProblem(password, confirmation);
  return problem.newPassword !== undefined || problem.confirmation !== undefined;
}

/**
 * A new password and its confirmation, with the rule checklist between them.
 *
 * Controlled: the caller holds both values and decides when to show problems
 * (`showProblems`, normally "a submit has been attempted").
 */
export function NewPasswordFields({
  password,
  confirmation,
  onPasswordChange,
  onConfirmationChange,
  showProblems,
  passwordLabel,
  confirmationLabel,
  isDisabled = false,
}: {
  readonly password: string;
  readonly confirmation: string;
  readonly onPasswordChange: (value: string) => void;
  readonly onConfirmationChange: (value: string) => void;
  readonly showProblems: boolean;
  /** Already translated; defaults to "New password". */
  readonly passwordLabel?: string;
  /** Already translated; defaults to "Confirm new password". */
  readonly confirmationLabel?: string;
  readonly isDisabled?: boolean;
}) {
  const { t } = useTranslation();
  const problem = showProblems ? newPasswordProblem(password, confirmation) : {};

  const message = (kind: NewPasswordProblem | undefined) =>
    kind === 'required'
      ? t('validation.required')
      : kind === 'rules'
        ? t('password.rulesNotMet')
        : kind === 'mismatch'
          ? t('auth.passwordMismatch')
          : undefined;

  const passwordError = message(problem.newPassword);
  const confirmationError = message(problem.confirmation);

  return (
    <div className="grid min-w-0 gap-stack">
      <PasswordField
        label={passwordLabel ?? t('auth.newPassword')}
        value={password}
        onChange={onPasswordChange}
        autoComplete="new-password"
        isRequired
        isDisabled={isDisabled}
        {...(passwordError ? { error: passwordError } : {})}
      />
      <PasswordRules value={password} />
      <PasswordField
        label={confirmationLabel ?? t('auth.confirmPassword')}
        value={confirmation}
        onChange={onConfirmationChange}
        autoComplete="new-password"
        isRequired
        isDisabled={isDisabled}
        {...(confirmationError ? { error: confirmationError } : {})}
      />
    </div>
  );
}
